import { useQueries, useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { lensOf, membershipOf, roleOf } from "~/access/membership";
import { accessAtOf, featuresQuery, meAccessQuery } from "~/access/payload";
import { isAdminViewAtom, selectedAccessRoleIdAtom } from "~/atoms";
import { useOrgs } from "~/hooks/useOrgs";
import { ALL_LOCATIONS, ALL_LOCATIONS_VALUE } from "~/utils/Constants";
import type { Org } from "~/utils/Types";

export type LocationOption = {
  value: string;
  label: string;
};

const CAMERA_FEATURE = "meq-camera";

/**
 * The current customer's other places (matched by idString prefix, e.g.
 * `JBS_*`) where the caller can see `feature`, each judged by its own access
 * payload as if the caller stood there. Without a feature, every sibling.
 */
export const useVisibleSiblings = (
  feature: string | undefined,
  isEnabled = true,
): { siblings: NonNullable<Org>[]; isPending: boolean } => {
  const { currentOrg, getOrgShortName, orgs } = useOrgs();
  const currentOrgIdString = currentOrg?.idString;
  const orgShortName = getOrgShortName();
  const isAdminView = useAtomValue(isAdminViewAtom);
  const selectedRoleId = useAtomValue(selectedAccessRoleIdAtom);

  const siblings = useMemo(
    () =>
      isEnabled
        ? (orgs ?? []).filter(
            (org): org is NonNullable<Org> =>
              !!org &&
              org.idString !== currentOrgIdString &&
              org.idString.startsWith(`${orgShortName}_`),
          )
        : [],
    [isEnabled, orgs, currentOrgIdString, orgShortName],
  );

  const isChecked = isEnabled && feature !== undefined;
  const featuresResult = useQuery({ ...featuresQuery(), enabled: isChecked });
  const payloads = useQueries({
    queries: siblings.map((org) => ({
      ...meAccessQuery(accessAtOf(org)),
      enabled: isChecked,
    })),
  });

  const isPending =
    isChecked &&
    (featuresResult.isPending || payloads.some((result) => result.isPending));

  // useQueries answers a fresh array every render; key the list on the flags.
  const visibleKey = siblings
    .map((_, index) => {
      if (!isChecked) return true;
      const payload = payloads[index]?.data;
      const features = featuresResult.data;
      if (!payload || !features) return false;
      const canSee = membershipOf(
        roleOf(payload, selectedRoleId),
        lensOf(payload.lens, isAdminView),
        features,
      );
      return canSee(feature);
    })
    .join(",");

  const visibleSiblings = useMemo(() => {
    if (isPending) return [];
    const visible = visibleKey.split(",");
    return siblings.filter((_, index) => visible[index] === "true");
  }, [isPending, siblings, visibleKey]);

  return { siblings: visibleSiblings, isPending };
};

/**
 * For an "All Locations" org, the sibling plants the caller can see
 * `requiredFeature` at. JBS Brazil additionally gets a wildcard "All
 * Locations" option that aggregates across plants, and uses the org's
 * displayName for the label (so `jbsbrazil_Campogrande` shows as "Campo
 * Grande").
 *
 * `isPending` holds until every sibling's access answer has arrived; until
 * then no plant is listed.
 *
 * @param requiredFeature The registry id of the feature the current page or
 *   tab shows. When omitted, JBS Brazil lists every plant (the server enforces
 *   per-feature access) and other orgs default to `meq-camera`.
 */
export const useAllLocations = (
  requiredFeature?: string,
): { locations: LocationOption[]; isPending: boolean } => {
  const { t } = useTranslation("dataAnalysisOverview");
  const { getOrgLocation, getOrgShortName } = useOrgs();
  const currentLocation = getOrgLocation();
  const orgShortName = getOrgShortName();
  const isAllLocationsOrg = currentLocation === ALL_LOCATIONS;
  const isJbsBrazil = orgShortName.toLowerCase() === "jbsbrazil";

  const { siblings, isPending } = useVisibleSiblings(
    requiredFeature ?? (isJbsBrazil ? undefined : CAMERA_FEATURE),
    isAllLocationsOrg,
  );

  const locations = useMemo(() => {
    if (!isAllLocationsOrg) {
      return [{ value: currentLocation, label: currentLocation }];
    }

    const plants: LocationOption[] = siblings.map((org) => {
      const value = org.idString.split("_")[1] ?? ALL_LOCATIONS;
      const label = isJbsBrazil
        ? org.displayName.replace(/^JBS Brazil\s*/i, "").trim()
        : value;
      return { value, label };
    });

    return isJbsBrazil
      ? [{ value: ALL_LOCATIONS_VALUE, label: t("allLocations") }, ...plants]
      : plants;
  }, [isAllLocationsOrg, currentLocation, isJbsBrazil, siblings, t]);

  return { locations, isPending };
};
